import logoUrl from "@/assets/images/cambre-logo.png";
import {
    AlignmentType,
    BorderStyle,
    Document,
    ImageRun,
    Packer,
    Paragraph,
    type ShadingType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
} from "docx";
import { formatCurrency } from "./currencyUtils";
import {
    type CostData,
    type DisbursementRow,
    calculateTotalSpecials,
    getExpenseAmount,
    mapInsuranceCompanies,
    mapMedicalProviders,
} from "./settlementStatementMapper";

// Logo cache to avoid redundant fetches
let cachedLogoData: Uint8Array | null = null;

interface SettlementStatementData {
    clientName: string;
    caseNumber: string;
    dateOfLoss: string;
    negotiatedLiability: number;
    negotiatedUIUIM: number;
    attorneyFeePercentage: number;
    attorneyFeeAmount: number;
    disbursementRows: DisbursementRow[];
    costData: CostData;
    clientDistributionAmount: number;
    selectedDocuments?: string[];
}

/**
 * Create letterhead section with logo
 * Logo image is 2350x315 (aspect ratio ~7.5:1)
 * Display at 600px wide to maintain quality and readability
 */
const createLetterheadSection = async (): Promise<Paragraph[]> => {
    // Cache logo to avoid redundant fetches
    if (!cachedLogoData) {
        const response = await fetch(logoUrl);
        const imageBuffer = await response.arrayBuffer();
        cachedLogoData = new Uint8Array(imageBuffer);
    }

    return [
        new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
                new ImageRun({
                    type: "png",
                    data: cachedLogoData,
                    transformation: {
                        width: 600, // Wide banner with attorney names and firm info
                        height: 80, // Maintain aspect ratio (600/7.5 ≈ 80)
                    },
                }),
            ],
        }),
    ];
};

/**
 * Create title paragraph
 */
const createTitleParagraph = (): Paragraph => {
    return new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
            new TextRun({
                text: "SETTLEMENT STATEMENT",
                bold: true,
                size: 28,
            }),
        ],
    });
};

/**
 * Create a table cell with text
 */
const createTableCell = (
    text: string,
    options: {
        bold?: boolean;
        alignment?: (typeof AlignmentType)[keyof typeof AlignmentType];
        columnSpan?: number;
        fontSize?: number;
        shading?: { fill: string; type: typeof ShadingType.SOLID };
    } = {},
): TableCell => {
    const {
        bold = false,
        alignment = AlignmentType.LEFT,
        columnSpan,
        fontSize = 20,
        shading,
    } = options;

    return new TableCell({
        children: [
            new Paragraph({
                alignment,
                children: [
                    new TextRun({
                        text,
                        bold,
                        size: fontSize,
                    }),
                ],
            }),
        ],
        columnSpan,
        shading,
        borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
        },
    });
};

/**
 * Create header row with standard black borders
 */
const createHeaderRow = (clientName: string, dateOfLoss: string, caseNumber: string): TableRow => {
    return new TableRow({
        children: [
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: "CLIENT NAME:",
                                bold: true,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    right: { style: BorderStyle.NONE, size: 0, color: "000000" },
                },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: clientName,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                    right: { style: BorderStyle.NONE, size: 0, color: "000000" },
                },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: "DATE OF LOSS:",
                                bold: true,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                    right: { style: BorderStyle.NONE, size: 0, color: "000000" },
                },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: dateOfLoss,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                    right: { style: BorderStyle.NONE, size: 0, color: "000000" },
                },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: "FILE NUMBER:",
                                bold: true,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                    right: { style: BorderStyle.NONE, size: 0, color: "000000" },
                },
            }),
            new TableCell({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.LEFT,
                        children: [
                            new TextRun({
                                text: caseNumber,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                },
            }),
        ],
    });
};

/**
 * Create settlement proceeds row
 */
const createSettlementProceedsRow = (totalProceeds: number): TableRow => {
    return new TableRow({
        children: [
            createTableCell("Total Settlement Proceeds:", { bold: true, columnSpan: 5 }),
            createTableCell(formatCurrency(totalProceeds), {
                bold: true,
                alignment: AlignmentType.RIGHT,
            }),
        ],
    });
};

/**
 * Create attorney fee row
 */
const createAttorneyFeeRow = (percentage: number, amount: number): TableRow => {
    return new TableRow({
        children: [
            createTableCell(`Less Attorney's Fees: ${percentage}% per Contract`, { columnSpan: 5 }),
            createTableCell(formatCurrency(amount), { alignment: AlignmentType.RIGHT }),
        ],
    });
};

/**
 * Create expenses row
 */
const createExpensesRow = (amount: number): TableRow => {
    return new TableRow({
        children: [
            new TableCell({
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "Less Expenses:",
                                size: 20,
                            }),
                        ],
                    }),
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "(Medical Records, photocopies, postage)",
                                size: 18,
                                italics: true,
                            }),
                        ],
                    }),
                ],
                columnSpan: 5,
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                },
            }),
            createTableCell(formatCurrency(amount), { alignment: AlignmentType.RIGHT }),
        ],
    });
};

/**
 * Create total specials row
 */
const createTotalSpecialsRow = (total: number): TableRow => {
    return new TableRow({
        children: [
            new TableCell({
                children: [
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "Total Specials ",
                                bold: true,
                                size: 20,
                            }),
                            new TextRun({
                                text: `(${formatCurrency(total)})`,
                                bold: true,
                                italics: true,
                                size: 20,
                            }),
                            new TextRun({
                                text: ":",
                                bold: true,
                                size: 20,
                            }),
                        ],
                    }),
                ],
                columnSpan: 5,
                borders: {
                    top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                    right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                },
            }),
            createTableCell("", { alignment: AlignmentType.RIGHT }),
        ],
    });
};

/**
 * Create medical provider row matching template format
 * Layout: Company Name | $ | Bill Amount | Description | $ | Reduction
 *         Acct: number (on second line if present)
 */
const createMedicalProviderRow = (
    company: string,
    bill: number,
    balanceDescription: string,
    reduction: number,
    accountNumber?: string | null,
): TableRow => {
    // First row with company name, bill, description, and reduction
    const mainRowCells = [
        // Company name (2 columns)
        new TableCell({
            children: [
                new Paragraph({
                    children: [
                        new TextRun({
                            text: company,
                            size: 20,
                        }),
                    ],
                }),
                ...(accountNumber
                    ? [
                          new Paragraph({
                              indent: { left: 100 },
                              children: [
                                  new TextRun({
                                      text: `Acct: ${accountNumber}`,
                                      size: 18,
                                  }),
                              ],
                          }),
                      ]
                    : []),
            ],
            columnSpan: 2,
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // $ sign
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: "$",
                            size: 20,
                        }),
                    ],
                }),
            ],
            width: { size: 5, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Bill amount
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: formatCurrency(bill).replace("$", "").trim(),
                            size: 20,
                        }),
                    ],
                }),
            ],
            width: { size: 15, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Description
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            text: balanceDescription,
                            size: 20,
                        }),
                    ],
                }),
            ],
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Reduction with $
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: formatCurrency(reduction),
                            size: 20,
                        }),
                    ],
                }),
            ],
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            },
        }),
    ];

    return new TableRow({
        children: mainRowCells,
    });
};

/**
 * Create insurance/loan company row matching template format
 * Layout: Company Name | $ | Bill Amount | Description | $ | Reduction
 *         Acct: number (on second line if present)
 */
const createInsuranceCompanyRow = (
    company: string,
    bill: number,
    statusDescription: string,
    reduction: number,
    accountNumber?: string | null,
): TableRow => {
    // First row with company name, bill, description, and reduction
    const mainRowCells = [
        // Company name (2 columns)
        new TableCell({
            children: [
                new Paragraph({
                    children: [
                        new TextRun({
                            text: company,
                            size: 20,
                        }),
                    ],
                }),
                ...(accountNumber
                    ? [
                          new Paragraph({
                              indent: { left: 100 },
                              children: [
                                  new TextRun({
                                      text: `Acct: ${accountNumber}`,
                                      size: 18,
                                  }),
                              ],
                          }),
                      ]
                    : []),
            ],
            columnSpan: 2,
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // $ sign
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: "$",
                            size: 20,
                        }),
                    ],
                }),
            ],
            width: { size: 5, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Bill amount
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: formatCurrency(bill).replace("$", "").trim(),
                            size: 20,
                        }),
                    ],
                }),
            ],
            width: { size: 15, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Description
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            text: statusDescription,
                            size: 20,
                        }),
                    ],
                }),
            ],
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.NONE, size: 0, color: "000000" },
            },
        }),
        // Reduction with $
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: formatCurrency(reduction),
                            size: 20,
                        }),
                    ],
                }),
            ],
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
                left: { style: BorderStyle.NONE, size: 0, color: "000000" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "000000" },
            },
        }),
    ];

    return new TableRow({
        children: mainRowCells,
    });
};

/**
 * Create net proceeds row
 */
const createNetProceedsRow = (amount: number): TableRow => {
    return new TableRow({
        children: [
            createTableCell("Net Proceeds to Client:", { bold: true, columnSpan: 5 }),
            createTableCell(formatCurrency(amount), {
                bold: true,
                alignment: AlignmentType.RIGHT,
            }),
        ],
    });
};

/**
 * Legal agreement text for settlement statements
 * Last reviewed: [Date to be filled in by legal team]
 * DO NOT MODIFY without legal department approval
 *
 * This text must appear on all settlement statements per firm policy.
 */
const SETTLEMENT_AGREEMENT_PARAGRAPH_1 =
    "The undersigned acknowledges that he/she has read, understood, and agreed to the above settlement statement. The undersigned further understands and agrees that no medical care providers or benefit providers, unless listed above, have been or will be paid out of this settlement, and that any such bills or right of reimbursement remain the sole responsibility of the client and that the client will hold attorneys harmless from any such amounts due.";

const SETTLEMENT_AGREEMENT_PARAGRAPH_2 =
    "I hereby declare that I fully understand the terms of this settlement. My attorney has explained to me the meaning of the full and final release that I am signing in connection with my claim. I voluntarily accept the settlement amount for the purpose of making a final compromise, adjustment, and settlement of all claims for injuries, losses and damages resulting or to result from said accident. I am not involved in bankruptcy proceedings at this time. My attorneys have fully explained the meaning and importance of the release that I have signed in connection with this case.";

/**
 * Create agreement header paragraph
 */
const createAgreementHeaderParagraph = (): Paragraph => {
    return new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 200 },
        children: [
            new TextRun({
                text: "CLIENT'S AGREEMENT REGARDING SETTLEMENT, DISBURSEMENT OF SETTLEMENT PROCEEDS,",
                bold: true,
                size: 22,
            }),
        ],
    });
};

/**
 * Create agreement header line 2
 */
const createAgreementHeaderLine2 = (): Paragraph => {
    return new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
            new TextRun({
                text: "AND PAYMENT OF MEDICAL CARE PROVIDERS OR BENEFIT PROVIDERS:",
                bold: true,
                size: 22,
            }),
        ],
    });
};

/**
 * Create agreement paragraph 1
 */
const createAgreementParagraph1 = (): Paragraph => {
    return new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 200 },
        children: [
            new TextRun({
                text: SETTLEMENT_AGREEMENT_PARAGRAPH_1,
                size: 22,
            }),
        ],
    });
};

/**
 * Create agreement paragraph 2
 */
const createAgreementParagraph2 = (): Paragraph => {
    return new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 200 },
        children: [
            new TextRun({
                text: SETTLEMENT_AGREEMENT_PARAGRAPH_2,
                size: 22,
            }),
        ],
    });
};

/**
 * Create signature line paragraphs
 */
const createSignatureParagraphs = (clientName: string): Paragraph[] => {
    const currentYear = new Date().getFullYear();
    return [
        new Paragraph({
            alignment: AlignmentType.LEFT,
            spacing: { before: 200, after: 100 },
            children: [
                new TextRun({
                    text: `READ, UNDERSTOOD AND AGREED, THIS _____ DAY OF _____________, ${currentYear}.`,
                    size: 22,
                }),
            ],
        }),
        new Paragraph({
            spacing: { before: 200, after: 100 },
            children: [
                new TextRun({
                    text: "",
                    size: 22,
                }),
            ],
        }),
        new Paragraph({
            spacing: { after: 100 },
            children: [
                new TextRun({
                    text: "",
                    size: 22,
                }),
            ],
        }),
        new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [
                new TextRun({
                    text: "_________________________________________________",
                    size: 22,
                }),
            ],
        }),
        new Paragraph({
            alignment: AlignmentType.LEFT,
            children: [
                new TextRun({
                    text: clientName,
                    size: 22,
                }),
            ],
        }),
    ];
};

/**
 * Generate Settlement Statement Word Document
 */
export const generateSettlementStatementDocx = async (
    data: SettlementStatementData,
): Promise<Blob> => {
    // Input validation
    if (!data.clientName?.trim()) {
        throw new Error("Client name is required for settlement statement");
    }
    if (!data.caseNumber?.trim()) {
        throw new Error("Case number is required for settlement statement");
    }
    if (!data.dateOfLoss?.trim()) {
        throw new Error("Date of loss is required for settlement statement");
    }
    if (data.negotiatedLiability < 0) {
        throw new Error("Negotiated liability cannot be negative");
    }
    if (data.negotiatedUIUIM < 0) {
        throw new Error("Negotiated UM/UIM cannot be negative");
    }
    if (data.attorneyFeeAmount < 0) {
        throw new Error("Attorney fee amount cannot be negative");
    }
    if (data.clientDistributionAmount < 0) {
        throw new Error("Client distribution amount cannot be negative");
    }

    const totalProceeds = data.negotiatedLiability + data.negotiatedUIUIM;
    const totalSpecials = calculateTotalSpecials(data.disbursementRows);
    const expenseAmount = getExpenseAmount(data.costData);
    const medicalProviders = mapMedicalProviders(data.disbursementRows);
    const insuranceCompanies = mapInsuranceCompanies(data.disbursementRows);

    const tableRows: TableRow[] = [];

    // Header row (orange background)
    tableRows.push(createHeaderRow(data.clientName, data.dateOfLoss, data.caseNumber));

    // Settlement Proceeds
    tableRows.push(createSettlementProceedsRow(totalProceeds));

    // Attorney Fee
    tableRows.push(createAttorneyFeeRow(data.attorneyFeePercentage, data.attorneyFeeAmount));

    // Expenses
    tableRows.push(createExpensesRow(expenseAmount));

    // Total Specials
    tableRows.push(createTotalSpecialsRow(totalSpecials));

    // Medical Providers
    for (const provider of medicalProviders) {
        tableRows.push(
            createMedicalProviderRow(
                provider.company,
                provider.bill,
                provider.balanceDescription,
                provider.reduction,
                provider.accountNumber,
            ),
        );
    }

    // Insurance Companies / Loans
    for (const company of insuranceCompanies) {
        tableRows.push(
            createInsuranceCompanyRow(
                company.company,
                company.bill,
                company.statusDescription,
                company.reduction,
                company.accountNumber,
            ),
        );
    }

    // Net Proceeds
    tableRows.push(createNetProceedsRow(data.clientDistributionAmount));

    const dataTable = new Table({
        rows: tableRows,
        width: {
            size: 100,
            type: WidthType.PERCENTAGE,
        },
    });

    // Load letterhead with logo
    const letterheadSection = await createLetterheadSection();

    // Build document sections
    const documentChildren: (Paragraph | Table)[] = [
        // Letterhead
        ...letterheadSection,
        // Title
        createTitleParagraph(),
        // Data table
        dataTable,
        // Agreement text
        createAgreementHeaderParagraph(),
        createAgreementHeaderLine2(),
        createAgreementParagraph1(),
        createAgreementParagraph2(),
        // Signature line
        ...createSignatureParagraphs(data.clientName),
    ];

    const doc = new Document({
        sections: [
            {
                children: documentChildren,
            },
        ],
    });

    return await Packer.toBlob(doc);
};

/**
 * Generate filename for settlement statement
 */
export const generateSettlementStatementFilename = (caseNumber: string): string => {
    const date = new Date().toISOString().split("T")[0];
    return `Settlement_Statement_${caseNumber.replace(/\s+/g, "_")}_${date}.docx`;
};
